import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class S3StorageService {
  constructor(private http: HttpClient) {}

  /**
   * Uploads a Base64 chart image to S3 using presigned PUT URL
   * @param presignedUrl Pre-signed PUT URL from backend
   * @param base64Data Base64 PNG string from ECharts
   */
  async uploadSvg(presignedUrl: string, base64Data: string): Promise<void> {
    try {
      const blob = this.decodeURIComponent(base64Data, 'image/svg+xml');

      const headers = new HttpHeaders({
        'Content-Type': 'image/svg+xml',
      });

      await firstValueFrom(this.http.put(presignedUrl, blob, { headers }));
    } catch (error) {
      console.error('Error uploading chart to S3:', error);
      throw error;
    }
  }

  /**
   * Deletes an object from S3 using presigned DELETE URL
   * @param presignedUrl Pre-signed DELETE URL from backend
   */
  async deletePresignedChartAndDashboard(presignedUrl: string): Promise<void> {
    try {
      await firstValueFrom(this.http.delete(presignedUrl));
    } catch (error) {
      console.error('Error deleting image from S3:', error);
      throw error;
    }
  }

  /**
   * Converts Base64 string to Blob
   */
  private decodeURIComponent(base64: string, contentType: string): Blob {
    const data = base64.split(',')[1];
    const decodedData = decodeURIComponent(data);

    return new Blob([decodedData], { type: contentType });
  }

  public download(cloudfrontUrl: string, filename: string) {
    this.http.get(cloudfrontUrl, { responseType: 'blob' }).subscribe((blob) => {
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(objectUrl);
    });
  }

  /**
   * Uploads a dashboard image to S3 using presigned PUT URL
   * @param presignedUrl Pre-signed PUT URL from backend
   * @param pngBlob PNG blob data from Html canvas
   */
  async uploadPng(presignedUrl: string, pngBlob: Blob): Promise<void> {
    try {
      const headers = new HttpHeaders({
        'Content-Type': 'image/png',
      });

      await firstValueFrom(this.http.put(presignedUrl, pngBlob, { headers }));
    } catch (error) {
      console.error('Error uploading PNG to S3:', error);
      throw error;
    }
  }

  /**
   * Uploads a dashboard pdf to S3 using presigned PUT URL
   * @param presignedUrl Pre-signed PUT URL from backend
   * @param pdfBlob PDF blob data from Html canvas
   */
  async uploadPdf(presignedUrl: string, pdfBlob: Blob): Promise<void> {
    try {
      const headers = new HttpHeaders({
        'Content-Type': 'application/pdf',
      });

      await firstValueFrom(this.http.put(presignedUrl, pdfBlob, { headers }));
    } catch (error) {
      console.error('Error uploading PDF to S3:', error);
      throw error;
    }
  }
}
